/**
 * Font configuration for stunnr-lite.
 *
 * Fonts here are split into two groups (Google fonts come from the DX font
 * catalogue at runtime, not this file):
 * 1. CDN Fonts — proprietary fonts hosted on the CB fonts CDN
 *    (cdn.stunnr.co/assets/fonts/), listed in the shared availableFonts.json
 *    and staged into the creativebuilder-api image, which renders these.
 * 2. System Fonts — web-safe fonts, no loading required in the browser.
 *    May render differently on the API server.
 */

import availableFonts from './availableFonts.json';
import type { FontConfig, FontGroup, ManifestFamily } from '@/types/library/font';

export const FONT_GROUPS = {
  GOOGLE: 'google',
  CDN: 'cdn',
  SYSTEM: 'system',
  UPLOADED: 'uploaded'
} as const;

// ── CDN Fonts (proprietary, loaded from CB fonts CDN, also on API server) ──

const toFontConfig = (families: ManifestFamily[]): FontConfig[] =>
  families.map(family => ({
    name: family.name,
    variants: (family.variants ?? []).map(variant => ({
      name: variant.name ?? String(variant.value),
      value: variant.value,
      styles: (variant.styles ?? []).map(entry => ({
        style:
          entry.value === 'italic' ? ('italic' as const) : ('normal' as const),
        src: entry.src
      }))
    }))
  }));

export const CDN_FONTS: FontConfig[] = toFontConfig(
  availableFonts as ManifestFamily[]
);

export const SYSTEM_FONTS: FontConfig[] = [
  {
    name: 'Arial',
    variants: [
      { name: 'Regular', value: 400, styles: [{ style: 'normal', src: '' }] },
      { name: 'Bold', value: 700, styles: [{ style: 'normal', src: '' }] }
    ]
  },
  {
    name: 'Comic Sans MS',
    variants: [
      { name: 'Regular', value: 400, styles: [{ style: 'normal', src: '' }] },
      { name: 'Bold', value: 700, styles: [{ style: 'normal', src: '' }] }
    ]
  },
  {
    name: 'Courier New',
    variants: [
      { name: 'Regular', value: 400, styles: [{ style: 'normal', src: '' }] },
      { name: 'Bold', value: 700, styles: [{ style: 'normal', src: '' }] }
    ]
  },
  {
    name: 'Georgia',
    variants: [
      { name: 'Regular', value: 400, styles: [{ style: 'normal', src: '' }] }
    ]
  },
  {
    name: 'Impact',
    variants: [
      { name: 'Regular', value: 400, styles: [{ style: 'normal', src: '' }] }
    ]
  },
  {
    name: 'Times New Roman',
    variants: [
      { name: 'Regular', value: 400, styles: [{ style: 'normal', src: '' }] }
    ]
  },
  {
    name: 'Trebuchet MS',
    variants: [
      { name: 'Regular', value: 400, styles: [{ style: 'normal', src: '' }] },
      { name: 'Bold', value: 700, styles: [{ style: 'normal', src: '' }] }
    ]
  },
  {
    name: 'Verdana',
    variants: [
      { name: 'Regular', value: 400, styles: [{ style: 'normal', src: '' }] },
      { name: 'Bold', value: 700, styles: [{ style: 'normal', src: '' }] }
    ]
  }
];

/** All fonts, for iteration */
export const ALL_FONTS: FontConfig[] = [...CDN_FONTS, ...SYSTEM_FONTS];

/** Flat name lists */
export const CDN_FONT_NAMES: string[] = CDN_FONTS.map(f => f.name);
export const SYSTEM_FONT_NAMES: string[] = SYSTEM_FONTS.map(f => f.name);

/**
 * Determine which group a font belongs to. Google fonts are no longer bundled —
 * they come from the DX catalogue and are treated as the non-CDN ("online")
 * group alongside system fonts.
 */
export function getFontGroup(fontFamily: string): FontGroup {
  if (CDN_FONT_NAMES.includes(fontFamily)) return 'cdn';
  return 'system';
}

/** Find the FontConfig for a given family name */
export function getFontConfig(fontFamily: string): FontConfig | undefined {
  return ALL_FONTS.find(f => f.name === fontFamily);
}

/** Check if a font supports italic for the given weight. Always true — italic
 *  is enabled for every font; browser/canvas synthesize italic when no real
 *  italic face is loaded. */
export function supportsItalic(
  _fontFamily: string,
  _fontWeight: number | string
): boolean {
  return true;
}

/** Default font for new text layers */
export const DEFAULT_FONT_FAMILY = 'Roboto';

/**
 * Formats accepted when a user uploads a custom font file.
 *
 * Validation relies on the `extensions` set: browsers report most font files
 * with a generic `application/octet-stream` MIME, so the extension (plus a
 * FontFace load test) is the real gate. The MIME set is kept for completeness
 * and parity with `SUPPORTED_MEDIA_FORMATS`.
 */
export const SUPPORTED_FONT_FORMATS = {
  mimeTypes: new Set([
    'font/ttf',
    'font/otf',
    'font/woff',
    'font/woff2',
    'application/font-sfnt',
    'application/x-font-ttf',
    'application/x-font-otf',
    'application/octet-stream'
  ]),
  extensions: new Set(['ttf', 'otf', 'woff', 'woff2'])
} as const;

// ── Variant-key ⇄ weight mappings (used by fontHelper) ──

/** Numeric/keyword variant weight → human label, e.g. 700 → "Bold". */
export const WEIGHT_LABELS: Record<string, string> = {
  100: 'Thin',
  200: 'Extra Light',
  300: 'Light',
  500: 'Medium',
  600: 'Semi Bold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
  regular: 'Regular',
  italic: 'Italic'
};

/** Sub-family weight word ("bold", "semibold", …) → numeric CSS weight. */
export const WEIGHT_BY_NAME: Record<string, number> = {
  thin: 100,
  hairline: 100,
  extralight: 200,
  ultralight: 200,
  light: 300,
  regular: 400,
  normal: 400,
  book: 400,
  medium: 500,
  semibold: 600,
  demibold: 600,
  bold: 700,
  extrabold: 800,
  ultrabold: 800,
  black: 900,
  heavy: 900
};
